import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import Dashboard from './components/Dashboard';
import AdminDashboard from './components/AdminDashboard';
import ImmigrationProfile from './components/ImmigrationProfile';
import WorkflowEngine from './components/WorkflowEngine';
import CommunityDataPanel from './components/CommunityDataPanel';
import RFEPanel from './components/RFEPanel';
import EligibilityChecker from './components/EligibilityChecker';
import PriorityDatesPage from './components/PriorityDatesPage';
import LegalPage from './components/LegalPages';
import BlogPage from './components/Blog';
import AttorneyAssistPurchase from './components/AttorneyAssistPurchase';
import AttorneyAssistTrack from './components/AttorneyAssistTrack';
import analytics from './lib/analytics';
import { CheckoutAddonModal, GlobalPurchaseChrome, PurchaseNavButton } from './components/CheckoutFlow';
import { requestPathwayPurchase, setPendingPurchase } from './lib/pathways';
import { API_BASE } from './lib/apiBase';

// Context for authentication
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const userData = localStorage.getItem('user');
      if (userData) {
        setUser(JSON.parse(userData));
      }
    }
    setLoading(false);
  }, []);

  const login = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Navigation Component
const Navigation = () => {
  const { user } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 site-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-3">
            <a href="/" className="flex items-center text-white hover:opacity-90 transition-opacity min-w-0">
              <span className="wordmark-mark" aria-hidden="true"></span>
              <span className="wordmark text-lg sm:text-xl text-white truncate">ImmigrateSmart</span>
            </a>

            <div className="desktop-nav-links hidden md:flex items-center space-x-6 lg:space-x-8">
              <a href="#how-it-works" className="text-white hover:text-gold transition-colors font-medium nav-link">How It Works</a>
              <a href="#intelligence" className="text-white hover:text-gold transition-colors font-medium nav-link">Intelligence</a>
              <a href="#attorney-assist" className="text-white hover:text-gold transition-colors font-medium nav-link hidden lg:inline">Attorney Assist</a>
              <a href="/eligibility" className="text-white hover:text-gold transition-colors font-medium nav-link">
                Check Eligibility
              </a>
              <a href="/blog" className="text-white hover:text-gold transition-colors font-medium nav-link">
                Blog
              </a>
            </div>

            <div className="flex items-center gap-3 sm:gap-4">
              <PurchaseNavButton />
              {user ? (
                <a
                  href={user?.is_admin ? "/admin/dashboard" : "/dashboard"}
                  className="hidden sm:inline-block border border-white/30 text-white px-3 sm:px-5 py-2 sm:py-2.5 text-[10px] sm:text-xs font-semibold tracking-widest uppercase hover:border-[#D4A017] hover:text-[#D4A017] transition-colors"
                >
                  Dashboard
                </a>
              ) : (
                <button
                  onClick={() => setShowAuth(true)}
                  className="text-white hover:text-gold transition-colors font-medium text-sm tracking-wide hidden sm:inline"
                >
                  Sign In
                </button>
              )}
              <button
                type="button"
                className="mobile-nav-toggle text-white border border-white/25 px-3 py-2 text-xs tracking-widest uppercase"
                aria-expanded={mobileOpen}
                aria-label="Toggle menu"
                onClick={() => setMobileOpen((v) => !v)}
              >
                {mobileOpen ? 'Close' : 'Menu'}
              </button>
            </div>
          </div>
        </div>

        <div className={`mobile-nav-panel ${mobileOpen ? 'is-open' : ''}`}>
          <button type="button" onClick={() => { closeMobile(); requestPathwayPurchase(); }} className="text-left text-[#D4A017] text-sm py-1 font-bold">
            Purchase a Pathway
          </button>
          <a href="#how-it-works" onClick={closeMobile} className="text-white text-sm py-1">How It Works</a>
          <a href="#intelligence" onClick={closeMobile} className="text-white text-sm py-1">Intelligence</a>
          <a href="#attorney-assist" onClick={closeMobile} className="text-white text-sm py-1">Attorney Assist</a>
          <a href="/eligibility" onClick={closeMobile} className="text-white text-sm py-1">Check Eligibility</a>
          <a href="/blog" onClick={closeMobile} className="text-white text-sm py-1">Blog</a>
          {!user && (
            <button
              onClick={() => { closeMobile(); setShowAuth(true); }}
              className="text-left text-white text-sm py-1"
            >
              Sign In
            </button>
          )}
          {user && (
            <>
              <a href="/community" onClick={closeMobile} className="text-white/80 text-sm py-1">Community</a>
              <a href="/rfe" onClick={closeMobile} className="text-white/80 text-sm py-1">RFE</a>
            </>
          )}
        </div>
      </nav>

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
    </>
  );
};

// Hero — brand first, one headline, one sentence, CTAs.
// Explainer video opens in a solid lightbox (never as a transparent hero overlay).
const HeroSection = () => {
  const videoRef = useRef(null);
  const [showDemo, setShowDemo] = useState(false);

  const openDemo = () => setShowDemo(true);

  const closeDemo = () => {
    const el = videoRef.current;
    if (el) {
      el.pause();
      el.currentTime = 0;
    }
    setShowDemo(false);
  };

  useEffect(() => {
    if (!showDemo) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeDemo();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const el = videoRef.current;
    if (el) {
      el.play().catch(() => {});
    }
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [showDemo]);

  return (
    <>
      <section id="heroFinal" role="banner" aria-label="ImmigrateSmart hero">
        <div className="hf-bg" aria-hidden="true" />
        <div className="hf-overlay" aria-hidden="true" />

        <div className="hf-content">
          <p className="hf-brand">Immigrate<span className="gold">Smart</span></p>
          <h1>Filled petitions. A workflow you can finish.</h1>
          <p>
            Attorney-built workflows for nine DHS-priority pathways — completed USCIS forms
            and supporting drafts at a fraction of traditional counsel.
          </p>
          <div className="hf-cta">
            <a href="/pricing" className="btn btn-gold" data-testid="start-my-toolkit">
              Purchase a Pathway
            </a>
            <a href="/eligibility" className="btn btn-outline">
              Not sure? Check Eligibility
            </a>
            <button
              type="button"
              className="btn btn-outline"
              onClick={openDemo}
              data-testid="watch-demo"
            >
              Watch Demo
            </button>
          </div>
        </div>
      </section>

      {showDemo && (
        <div
          className="demo-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="ImmigrateSmart product demo"
        >
          <div className="demo-lightbox-backdrop" onClick={closeDemo} aria-hidden="true" />
          <div className="demo-lightbox-panel">
            <div className="demo-lightbox-header">
              <p className="demo-lightbox-title">Product demo</p>
              <button
                type="button"
                className="demo-lightbox-close"
                onClick={closeDemo}
                aria-label="Close demo video"
              >
                <i className="fas fa-times" aria-hidden="true" />
                <span>Close</span>
              </button>
            </div>
            <div className="demo-lightbox-player">
              <video
                ref={videoRef}
                className="demo-lightbox-video"
                controls
                playsInline
                preload="metadata"
                poster="/videos/dashboard-poster.jpg"
              >
                <source src="/videos/dashboard.mp4" type="video/mp4" />
              </video>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Proof strip — one job, no metric-card clutter
const TrustSection = () => {
  return (
    <section className="section-y bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-14">
          <h2 className="text-4xl md:text-5xl font-semibold text-[#222226] mb-5 font-cormorant">
            Built the way a practice works
          </h2>
          <p className="text-lg text-[#3F3C38] max-w-2xl mx-auto leading-relaxed">
            Line-by-line petition forms, a persistent immigration profile,
            and a free eligibility check before you buy.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-0 border-t border-b border-[#222226]/20 divide-y md:divide-y-0 md:divide-x divide-[#222226]/20">
          {[
            ['Nine pathways', 'Family, employment, asylum, student, and DV — ordered by DHS filing volume.'],
            ['Documents you can file', 'Pre-populated USCIS PDF and DOCX from your workflow answers, plus supporting drafts.'],
            ['Eligibility first', 'Free ranked pathway matches. Optional — never a gate to purchase.'],
          ].map(([title, body]) => (
            <div key={title} className="px-6 py-10 animate-on-scroll">
              <h3 className="font-cormorant text-2xl text-[#222226] mb-3">{title}</h3>
              <p className="text-sm text-[#3F3C38] leading-relaxed">{body}</p>
            </div>
          ))}
        </div>

        <blockquote className="mt-16 max-w-2xl mx-auto testimonial-card animate-on-scroll">
          <p className="text-[#222226] text-xl font-cormorant italic leading-snug mb-4">
            “The NIW workflow turned a sprawling evidence problem into a clear filing plan —
            without the billable-hour fog.”
          </p>
          <footer className="text-sm text-[#3F3C38]">
            <span className="font-semibold text-[#222226]">Research scientist</span>
            <span className="mx-2">·</span>
            EB-2 NIW filer
          </footer>
        </blockquote>
      </div>
    </section>
  );
};

// Authentication Modal Component with Premium Design
const AuthModal = ({ onClose }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: ''
  });
  const [resetEmail, setResetEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { login } = useAuth();

  const getPasswordStrength = (password) => {
    let score = 0;
    if (password.length >= 8) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^\w]/.test(password)) score++;
    return score;
  };

  const passwordsMatch = formData.password === formData.confirmPassword;
  const passwordStrength = getPasswordStrength(formData.password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!isLogin && !passwordsMatch) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
      const response = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Authentication failed');
      }

      login(data.user, data.access_token);

      // Import eligibility answers saved before account creation (AC 33)
      try {
        const answersRaw = localStorage.getItem('eligibility_answers');
        if (answersRaw) {
          const answers = JSON.parse(answersRaw);
          await fetch(`${API_BASE}/api/profile/import-eligibility`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${data.access_token}`,
            },
            body: JSON.stringify({ responses: answers }),
          });
          localStorage.removeItem('eligibility_answers');
          localStorage.removeItem('eligibility_results');
        }
      } catch (importErr) {
        console.warn('Eligibility import skipped:', importErr);
      }

      onClose();
      const pendingPathway =
        localStorage.getItem('pending_purchase') ||
        localStorage.getItem('eligibility_pending_pathway');
      if (pendingPathway) {
        // Resume Stripe checkout on dashboard (do not skip payment → workflow)
        window.location.href = `/dashboard?purchase=${encodeURIComponent(pendingPathway)}`;
      } else {
        window.location.href = data.user?.is_admin ? '/admin/dashboard' : '/dashboard';
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_BASE}/api/auth/password-reset-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: resetEmail }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Password reset request failed');
      }

      if (data.email_delivery === 'unavailable') {
        setError(data.message);
      } else {
        setSuccess(data.message || 'If that email is on file, password reset instructions have been sent.');
      }

      // Track analytics event
      analytics.trackPasswordResetRequest(resetEmail);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[#303035]/80 flex items-center justify-center z-50 p-4 modal-backdrop">
      <div className="bg-[#FFFFFF] p-8 w-full max-w-md fade-in luxury-card">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold text-[#222226] font-cormorant">
            {showForgotPassword ? 'Reset Password' : (isLogin ? 'Welcome back' : 'Create account')}
          </h2>
          <button
            onClick={onClose}
            className="text-[#3F3C38] hover:text-[#222226] transition-colors"
            data-testid="close-auth-modal"
          >
            ✕
          </button>
        </div>

        {showForgotPassword ? (
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Email Address</label>
              <input
                type="email"
                data-testid="reset-email-input"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="Enter your email address"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold transition-all"
                required
              />
            </div>

            {error && (
              <div className="text-red-600 text-sm status-error p-3 rounded" data-testid="reset-error">
                {error}
              </div>
            )}

            {success && (
              <div className="text-green-600 text-sm status-success p-3 rounded" data-testid="reset-success">
                {success}
              </div>
            )}

            <button
              type="submit"
              data-testid="reset-submit-btn"
              disabled={loading}
              className="w-full bg-gold text-black py-3 rounded-lg font-semibold hover:bg-gold/90 transition-colors duration-300 disabled:opacity-50 btn-premium"
            >
              {loading ? 'Sending...' : 'Request reset'}
            </button>

            <button
              type="button"
              onClick={() => {
                setShowForgotPassword(false);
                setError('');
                setSuccess('');
              }}
              className="w-full text-gray-600 hover:text-gray-800 font-semibold transition-colors"
              data-testid="back-to-login"
            >
              Back to Sign In
            </button>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Full Name</label>
                <input
                  type="text"
                  data-testid="full-name-input"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold transition-all"
                  required
                />
              </div>
            )}

            <div>
              <label className="block text-gray-700 font-semibold mb-2">Email</label>
              <input
                type="email"
                data-testid="email-input"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold transition-all"
                required
              />
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  data-testid="password-input"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold transition-all"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  data-testid="toggle-password-visibility"
                >
                  <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>
              {!isLogin && formData.password && (
                <div className="mt-2">
                  <div className="flex space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <div
                        key={i}
                        className={`h-1 w-full rounded transition-all ${i < passwordStrength ? 'bg-gold' : 'bg-gray-200'
                          }`}
                      ></div>
                    ))}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    Password strength: {passwordStrength < 3 ? 'Weak' : passwordStrength < 4 ? 'Medium' : 'Strong'}
                  </div>
                </div>
              )}
            </div>

            {!isLogin && (
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Confirm Password</label>
                <input
                  type="password"
                  data-testid="confirm-password-input"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-gold focus:border-gold transition-all ${formData.confirmPassword && !passwordsMatch ? 'border-red-300' : 'border-gray-300'
                    }`}
                  required
                />
                {formData.confirmPassword && !passwordsMatch && (
                  <div className="text-red-600 text-sm mt-1">Passwords do not match</div>
                )}
              </div>
            )}

            {isLogin && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(true);
                    setError('');
                  }}
                  className="text-gold hover:text-gold/80 text-sm font-semibold transition-colors"
                  data-testid="forgot-password-link"
                >
                  Forgot Password?
                </button>
              </div>
            )}

            {error && (
              <div className="text-red-600 text-sm status-error p-3 rounded" data-testid="auth-error">
                {error}
              </div>
            )}

            {success && (
              <div className="text-green-600 text-sm status-success p-3 rounded" data-testid="auth-success">
                {success}
              </div>
            )}

            <button
              type="submit"
              data-testid="auth-submit-btn"
              disabled={loading || (!isLogin && !passwordsMatch)}
              className="w-full bg-gold text-black py-3 rounded-lg font-semibold hover:bg-gold/90 transition-colors duration-300 disabled:opacity-50 btn-premium"
            >
              {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
            </button>
          </form>
        )}

        {!showForgotPassword && (
          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-gold hover:text-gold/80 font-semibold transition-colors"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Pricing Section Component — all 9 pathways in DHS volume order
const PricingSection = () => {
  const [showAuth, setShowAuth] = useState(false);
  const [checkoutAddon, setCheckoutAddon] = useState(null); // pathway object pending checkout

  // All 9 pathways in DHS FY2023 volume order with locked M2 prices
  const pathways = [
    {
      id: 'family-i130',
      name: 'Family-Based I-130',
      price: '$299',
      priceNum: 299,
      description: 'Sponsor an immediate relative or family member for a green card',
      icon: 'fa-users',
      features: ['I-130 petition workflow', 'Relationship evidence guide', 'Supporting narrative draft', 'Form pre-population (PDF/DOCX)'],
      badge: null,
    },
    {
      id: 'aos-i485',
      name: 'Adjustment of Status',
      price: '$199',
      priceNum: 199,
      description: 'Apply for your green card without leaving the US (I-485)',
      icon: 'fa-id-card',
      features: ['I-485 workflow', '$100 with qualifying petition', 'Form pre-population (PDF/DOCX)', 'Document generation'],
      badge: null,
      note: 'Qualifying petition holders pay $100',
    },
    {
      id: 'perm-employer',
      name: 'PERM / Employer-Sponsored',
      price: '$399',
      priceNum: 399,
      description: 'Employer-sponsored green card via labor certification (ETA-9089)',
      icon: 'fa-briefcase',
      features: ['ETA-9089 workflow', 'Labor market test guidance', 'Prevailing wage documentation', 'AOS add-on eligible'],
      badge: null,
    },
    {
      id: 'eb2-niw',
      name: 'EB-2 National Interest Waiver',
      price: '$499',
      priceNum: 499,
      description: 'Self-petition for advanced degree professionals of national importance',
      icon: 'fa-graduation-cap',
      features: ['Dhanasar three-prong analysis', 'NIW personal statement draft', 'Evidence checklist (7 criteria)', 'Form pre-population (PDF/DOCX)'],
      badge: 'Most Popular',
    },
    {
      id: 'eb1a',
      name: 'EB-1A Extraordinary Ability',
      price: '$499',
      priceNum: 499,
      description: 'Self-petition for individuals with extraordinary ability in their field',
      icon: 'fa-trophy',
      features: ['10-criteria evidence analysis', 'Extraordinary ability narrative', 'Evidence checklist (10 criteria)', 'Form pre-population (PDF/DOCX)'],
      badge: null,
    },
    {
      id: 'asylum',
      name: 'Asylum & Humanitarian',
      price: '$299',
      priceNum: 299,
      description: 'Apply for asylum protection based on persecution or humanitarian grounds',
      icon: 'fa-shield-alt',
      features: ['I-589 workflow', 'Personal declaration draft', 'Country condition guidance', 'AOS add-on eligible'],
      badge: null,
    },
    {
      id: 'f1-change-status',
      name: 'F-1 / Change of Status',
      price: '$249',
      priceNum: 249,
      description: 'Change your nonimmigrant status or maintain F-1 student status',
      icon: 'fa-university',
      features: ['I-539 / I-20 workflow', 'Personal statement draft', 'Status maintenance tracking', 'Document generation'],
      badge: null,
    },
    {
      id: 'o1-nonimmigrant',
      name: 'O-1 Extraordinary Ability',
      price: '$349',
      priceNum: 349,
      description: 'Nonimmigrant visa for individuals with extraordinary ability or achievement',
      icon: 'fa-star',
      features: ['I-129 petition workflow', 'O-1 support letter draft', 'Evidence evaluation (8 criteria)', 'Form pre-population (PDF/DOCX)'],
      badge: null,
    },
    {
      id: 'dv-lottery',
      name: 'Diversity Visa (DV Lottery)',
      price: '$299',
      priceNum: 299,
      description: 'DS-5501 and immigrant visa process for DV lottery selectees',
      icon: 'fa-globe',
      features: ['DS-5501 workflow', 'Supporting statement draft', 'AOS add-on eligible', 'Document generation'],
      badge: null,
    },
  ];

  const initiateCheckout = (pathway) => {
    analytics.trackCheckoutStart(pathway.id);
    setPendingPurchase(pathway.id);
    const token = localStorage.getItem('token');
    if (!token) {
      setShowAuth(true);
      return;
    }
    setCheckoutAddon(pathway);
  };

  const handleConfirmCheckout = async ({ attorney_review, aos_addon }) => {
    const pathway = checkoutAddon;
    setCheckoutAddon(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/billing/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          pathway_id: pathway.id,
          attorney_review,
          aos_addon,
        }),
      });
      const data = await response.json();
      if (response.ok && data.url) {
        window.location.href = data.url;
      } else {
        const errorMsg = data.detail?.message || data.detail || data.message || 'Failed to create checkout session. Please try again.';
        alert(errorMsg);
      }
    } catch (error) {
      console.error('Purchase error:', error);
      alert('An error occurred. Please try again.');
    }
  };

  return (
    <>
      <section id="pricing" className="section-y bg-[#3A3A3F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="mb-14 max-w-2xl">
            <h2 className="heading-1 text-white mb-4 font-cormorant">
              Nine pathways. Locked prices.
            </h2>
            <p className="body-large text-white/80">
              One-time pathway purchase. Workflow, immigration profile, and generated documents included. No subscriptions.
            </p>
            <p className="mt-5 text-white/75 text-sm">
              Know your pathway? Purchase below — no quiz required.{' '}
              <a href="/eligibility" className="text-[#D4A017] hover:text-[#E8B41F] border-b border-[#D4A017] pb-0.5">
                Not sure? Check eligibility first
              </a>
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
            {pathways.map((pathway) => (
              <div
                key={pathway.id}
                className={`pathway-tile ${pathway.badge ? 'is-featured' : ''}`}
              >
                <div className="flex items-baseline justify-between gap-3 mb-3">
                  <h3 className="path-name">{pathway.name}</h3>
                  <div className="path-price">{pathway.price}</div>
                </div>
                {pathway.badge && (
                  <div className="text-[10px] tracking-[0.14em] uppercase text-[#D4A017] mb-2 font-semibold">
                    {pathway.badge}
                  </div>
                )}
                <p className="path-desc text-sm mb-4 leading-relaxed">{pathway.description}</p>
                {pathway.note && (
                  <p className="path-note text-xs mb-4 border-l-2 pl-3">
                    {pathway.note}
                  </p>
                )}
                <ul className="space-y-1.5 mb-6">
                  {pathway.features.slice(0, 4).map((f, i) => (
                    <li key={i} className="path-feature text-sm pl-3 border-l">
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => {
                    analytics.trackProductView(pathway.id);
                    initiateCheckout(pathway);
                  }}
                  className="path-cta path-cta-purchase"
                >
                  Purchase {pathway.price}
                </button>
              </div>
            ))}
          </div>

          <p className="text-sm text-white/70 max-w-3xl">
            AOS add-on $100 with a qualifying petition. Additional tools (case strength, deadlines, RFE, community data, priority dates) are in beta — visible after purchase, not sold as finished product.
          </p>
        </div>
      </section>

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
      {checkoutAddon && (
        <CheckoutAddonModal
          pathway={checkoutAddon}
          onClose={() => setCheckoutAddon(null)}
          onConfirm={handleConfirmCheckout}
        />
      )}
    </>
  );
};

// How It Works Section Component
const HowItWorksSection = () => {
  const steps = [
    {
      n: '01',
      title: 'Choose and purchase',
      body: 'Pick your pathway and pay once — or take the free eligibility check first if you are unsure. Eligibility is optional, never a gate.',
    },
    {
      n: '02',
      title: 'Build the case',
      body: 'Schema-driven workflow with save/resume. Fill the petition the way a practice would.',
    },
    {
      n: '03',
      title: 'Generate documents',
      body: 'Filled petition PDF, editable DOCX, and supporting narratives drafted only from your data.',
    },
    {
      n: '04',
      title: 'Download and file',
      body: 'Take the filled petition PDF, editable DOCX, and supporting drafts. Optional add-ons after filing are coming soon.',
    },
  ];

  return (
    <section id="how-it-works" className="section-y bg-[#48484E]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-5">
            <h2 className="heading-1 text-white mb-5 font-cormorant">
              How it works
            </h2>
            <p className="body-large text-white/80 max-w-md">
              From free eligibility check to filed petition — continuous coverage, not episodic billable hours.
            </p>
          </div>
          <div className="lg:col-span-7 space-y-0">
            {steps.map((s) => (
              <div key={s.n} className="intel-row animate-on-scroll" style={{ borderColor: 'rgba(255,255,255,0.18)' }}>
                <div className="intel-num">{s.n}</div>
                <div>
                  <h3 className="font-cormorant text-2xl text-white mb-2">{s.title}</h3>
                  <p className="text-sm text-white/75 leading-relaxed max-w-lg">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const IntelligenceSection = () => {
  return (
    <section id="intelligence" className="section-y bg-[#F4F3F0]">
      <div className="max-w-4xl mx-auto px-6">
        <div className="mb-12">
          <h2 className="text-4xl md:text-5xl font-semibold text-[#222226] mb-4 font-cormorant">
            Intelligence no attorney automates
          </h2>
          <p className="text-lg text-[#3F3C38] max-w-xl leading-relaxed">
            Eligibility, forms, and documents — available now. Additional case intelligence is coming soon.
          </p>
        </div>
        {[
          ['01', 'Eligibility Checker', 'Free ranked pathway matches before you buy. Answers pre-populate profile and workflow.'],
          ['02', 'Workflow & documents', 'Nine pathway schemas, persistent profile, filled USCIS PDF/DOCX, and supporting drafts.'],
          ['03', 'Coming soon', 'Case strength, deadlines, RFE response, community data, and priority dates — visible in beta, not sold as finished product.'],
        ].map(([n, title, body]) => (
          <div key={n} className="intel-row animate-on-scroll">
            <div className="intel-num">{n}</div>
            <div>
              <h3 className="font-cormorant text-2xl text-[#222226] mb-1">{title}</h3>
              <p className="text-sm text-[#3F3C38] leading-relaxed">{body}</p>
            </div>
          </div>
        ))}
        <div className="mt-12">
          <a
            href="/eligibility"
            className="inline-block bg-[#3A3A3F] text-[#FFFFFF] px-7 py-3 text-xs font-semibold tracking-widest uppercase hover:bg-[#D4A017] hover:text-[#222226] transition-colors"
          >
            Check eligibility — free
          </a>
        </div>
      </div>
    </section>
  );
};

// Attorney Assist Section Component
const AttorneyAssistSection = () => {
  const [showConsultationForm, setShowConsultationForm] = useState(false);

  return (
    <section id="attorney-assist" className="section-y bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="relative animate-on-scroll">
            <img
              src="https://customer-assets.emergentagent.com/job_smart-immigration/artifacts/gxyq2nph_image.png"
              alt="Immigration attorney reviewing a petition"
              className="w-full object-cover max-h-[520px] grayscale-[15%]"
            />
            <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/85 to-transparent">
              <p className="text-white font-cormorant text-xl italic">
                Clarity before filing. Counsel when it counts.
              </p>
            </div>
          </div>

          <div>
            <h2 className="heading-1 text-[#222226] mb-5 font-cormorant">
              Attorney Assist
            </h2>
            <p className="body-large text-[#3F3C38] mb-8">
              Upload your draft and get a trackable UOA Legal review — Simple $99 · Mid $149 · Complex $199.
              48-hour delivery target. No login required.
            </p>

            <ul className="space-y-5 mb-10 border-t border-[#222226]/15 pt-6">
              {[
                ['Draft review', 'Licensed counsel at UOA Legal marks up your petition before you file.'],
                ['Track like FedEx', 'Pay once, get a unique /track link — status updates as review moves.'],
                ['Tiered pricing', 'Simple $99 · Mid $149 · Complex $199 — choose the tier that fits your case.'],
              ].map(([t, b]) => (
                <li key={t} className="grid grid-cols-1 sm:grid-cols-[7rem_1fr] gap-1 sm:gap-4">
                  <span className="text-xs tracking-widest uppercase text-[#B8860B] font-semibold pt-1">{t}</span>
                  <span className="text-sm text-[#3F3C38] leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap gap-3">
              <a href="/attorney-assist" className="btn-primary">
                Start Attorney Assist
              </a>
              <button
                type="button"
                onClick={() => setShowConsultationForm(true)}
                className="border border-[#222226]/30 text-[#222226] px-6 py-3 text-xs font-semibold tracking-widest uppercase hover:border-[#D4A017] hover:text-[#B8860B] transition-colors"
              >
                Request consultation
              </button>
            </div>
          </div>
        </div>
      </div>

      {showConsultationForm && (
        <ConsultationModal onClose={() => setShowConsultationForm(false)} />
      )}
    </section>
  );
};

// Consultation Modal Component
const ConsultationModal = ({ onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    case_type: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/consultation/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSuccess(true);
        // Track analytics event
        if (window.analytics) {
          window.analytics.track('consult_requested', formData);
        }
      }
    } catch (error) {
      console.error('Consultation request error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-[#303035]/75 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-check text-green-600 text-2xl"></i>
          </div>
          <h3 className="text-2xl font-bold text-black mb-4">Request Received!</h3>
          <p className="text-gray-600 mb-6">
            We'll contact you within 24 hours to schedule your consultation.
          </p>
          <button onClick={onClose} className="btn-primary">
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-[#303035]/75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-black font-cormorant">Book Consultation</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <i className="fas fa-times text-xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Full Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold"
              required
            />
          </div>

          <div>
            <label className="block text-gray-700 font-semibold mb-2">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold"
              required
            />
          </div>

          <div>
            <label className="block text-gray-700 font-semibold mb-2">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold"
              required
            />
          </div>

          <div>
            <label className="block text-gray-700 font-semibold mb-2">Case Type</label>
            <select
              value={formData.case_type}
              onChange={(e) => setFormData({ ...formData, case_type: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold"
              required
            >
              <option value="">Select case type</option>
              <option value="eb2-niw">EB-2 NIW</option>
              <option value="asylum">Asylum</option>
              <option value="family-based">Family-based Immigration</option>
              <option value="business-visa">Business Visa</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-gray-700 font-semibold mb-2">Message</label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              rows="3"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold"
              placeholder="Tell us about your situation..."
            ></textarea>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gold text-black py-3 rounded-lg font-semibold hover:bg-gold/90 transition-colors disabled:opacity-50"
          >
            {loading ? 'Submitting...' : 'Book a Consultation'}
          </button>
        </form>
      </div>
    </div>
  );
};

// Dedicated pricing page — works for logged-in and logged-out users
const PricingPage = () => {
  useEffect(() => {
    analytics.trackPageView('pricing');
  }, []);

  return (
    <div className="min-h-screen bg-[#3A3A3F]">
      <Navigation />
      <div className="pt-20 sm:pt-24">
        <PricingSection />
      </div>
      <footer className="bg-[#3A3A3F] border-t border-white/10 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center text-white/60 text-sm">
          <p>
            Already know your pathway? Purchase above — eligibility is optional.{' '}
            <a href="/eligibility" className="text-[#D4A017] hover:text-[#E8B41F]">
              Check eligibility
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

// Landing Page Component
const LandingPage = () => {
  const [showAuth, setShowAuth] = useState(false);

  useEffect(() => {
    // Track page view
    analytics.trackPageView('home');

    // Resume auth from eligibility / purchase deep-links
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('auth') === '1' || params.get('from') === 'eligibility') {
        setShowAuth(true);
      }
    } catch { /* ignore */ }

    // Safety guard: keep only one hero instance active
    const heroes = document.querySelectorAll('.hero');
    for (let i = 1; i < heroes.length; i++) {
      heroes[i].remove();
    }

    // Intersection Observer for scroll animations
    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-[#3A3A3F]">
      <Navigation />
      <HeroSection />
      <TrustSection />
      <PricingSection />
      <HowItWorksSection />
      <IntelligenceSection />
      <AttorneyAssistSection />

      {/* Footer */}
      <footer className="bg-[#3A3A3F] border-t border-white/10 section-y">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-8 md:gap-10">
            <div>
              <div className="flex items-center mb-4">
                <span className="wordmark-mark" aria-hidden="true"></span>
                <span className="wordmark text-xl text-white">ImmigrateSmart</span>
              </div>
              <p className="text-white/70 text-sm leading-relaxed mb-3">Legal mastery made human.</p>
              <p className="text-[#D4A017] text-xs tracking-widest uppercase">Precision. Discretion. Distinction.</p>
            </div>

            <div>
              <h4 className="text-white text-xs tracking-widest uppercase mb-4 font-semibold">Pathways</h4>
              <ul className="space-y-2.5 text-white/70 text-sm">
                <li><a href="/pricing" className="hover:text-[#D4A017] transition-colors">All 9 Pathways</a></li>
                <li><a href="/eligibility" className="hover:text-[#D4A017] transition-colors">Eligibility Checker</a></li>
                <li><a href="/blog" className="hover:text-[#D4A017] transition-colors">Blog</a></li>
                <li><a href="#intelligence" className="hover:text-[#D4A017] transition-colors">Intelligence Layer</a></li>
                <li><a href="#attorney-assist" className="hover:text-[#D4A017] transition-colors">Attorney Review</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white text-xs tracking-widest uppercase mb-4 font-semibold">Support</h4>
              <ul className="space-y-2.5 text-white/70 text-sm">
                <li><a href="#how-it-works" className="hover:text-[#D4A017] transition-colors">How It Works</a></li>
                <li><a href="#attorney-assist" className="hover:text-[#D4A017] transition-colors">Attorney Assist</a></li>
                <li><a href="/blog" className="hover:text-[#D4A017] transition-colors">Blog</a></li>
                <li><a href="/eligibility" className="hover:text-[#D4A017] transition-colors">Eligibility Checker</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white text-xs tracking-widest uppercase mb-4 font-semibold">Legal</h4>
              <ul className="space-y-2.5 text-white/60 text-sm">
                <li><a href="/legal/privacy" className="hover:text-[#D4A017] transition-colors">Privacy Policy</a></li>
                <li><a href="/legal/terms" className="hover:text-[#D4A017] transition-colors">Terms of Service</a></li>
                <li><a href="/legal/disclaimer" className="hover:text-[#D4A017] transition-colors">Not legal advice</a></li>
              </ul>
            </div>
          </div>

          <div className="section-divider"></div>

          <div className="text-center text-white/60 text-sm">
            <p>&copy; {new Date().getFullYear()} ImmigrateSmart. All rights reserved.</p>
            <p className="mt-2 text-xs">This platform provides legal information, not legal advice. Consult with a qualified attorney for legal advice.</p>
          </div>
        </div>
      </footer>

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
    </div>
  );
};

// ProtectedRoute: redirect to landing page if not authenticated
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#3A3A3F] flex items-center justify-center">
        <div className="text-gold text-2xl">
          <i className="fas fa-spinner fa-spin mr-3"></i>Loading...
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return children;
};

// Main App Component
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <GlobalPurchaseChrome />
        <Routes>
          <Route path="/" element={<LandingPageOrDashboard />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/eligibility" element={<EligibilityChecker />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/*" element={<BlogPage />} />
          <Route path="/attorney-assist" element={<AttorneyAssistPurchase />} />
          <Route path="/track/:orderId" element={<AttorneyAssistTrack />} />
          <Route path="/legal/:page" element={<LegalPage />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ImmigrationProfile /></ProtectedRoute>} />
          <Route path="/workflow/:pathwayId" element={<ProtectedRoute><WorkflowEngine /></ProtectedRoute>} />
          <Route path="/community" element={<ProtectedRoute><CommunityDataPanel /></ProtectedRoute>} />
          <Route path="/rfe" element={<ProtectedRoute><RFEPanel /></ProtectedRoute>} />
          <Route path="/priority-dates" element={<ProtectedRoute><PriorityDatesPage /></ProtectedRoute>} />
          <Route path="*" element={<LandingPage />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

const LandingPageOrDashboard = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#3A3A3F] flex items-center justify-center">
        <div className="text-gold text-2xl premium-loading p-8 rounded-lg">
          <i className="fas fa-spinner fa-spin mr-3"></i>
          Loading your experience...
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to={user.is_admin ? '/admin/dashboard' : '/dashboard'} replace />;
  }

  return <LandingPage />;
};

export default App;